import {
    Button,
    ConfigProvider,
    Popconfirm,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import { Footer } from "antd/es/layout/layout";
import frFR from "antd/locale/fr_FR";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import {
    frenchDate,
    frenchDateSimple,
} from "../../../variables/functions";

// Libellés des types de proposition (code d'avenant -> texte lisible).
const TYPE_AVENANT_LABELS = {
    AN: "Affaire nouvelle",
    RN: "Renouvellement",
    PR: "Prorogation",
    IN: "Incorporation",
    RV: "Remise en vigueur",
    AG: "Augmentation de garantie",
    DG: "Diminution de garantie",
    TG: "Transfert de garanties",
    SP: "Suspension",
    RE: "Report d'effet",
};

// Vue Corbeille : propositions annulées (manuellement) ou déplacées automatiquement
// par le job planifié. Le backend renvoie les contrats au statut CORBEILLE.
const CorbeillePropositions = () => {
    const [data, setData] = useState([]);
    const [loadingContrats, setLoadingContrats] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, showSizeChanger: true });
    const [restoreLoadingId, setRestoreLoadingId] = useState(null);

    const navigate = useNavigate();

    // Restaure une proposition depuis la corbeille (retire le drapeau corbeille).
    const handleRestore = (codeId) => {
        setRestoreLoadingId(codeId);
        return axiosInstance
            .put(`/api/contrat/proposition/restaurer/${codeId}`)
            .then(() => {
                toast.success("Proposition restaurée.");
                setData((prev) => prev.filter((row) => row.codeId !== codeId));
                fetchData({ pagination });
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "Erreur lors de la restauration";
                toast.error(msg);
            })
            .finally(() => setRestoreLoadingId(null));
    };

    const columns = [
        { title: "Numero de devis", dataIndex: "numeroPolice", key: "1" },
        {
            title: "Type",
            dataIndex: "typeProposition",
            key: "type",
            render: (_, record) => (
                <Tag color={record.codeAvenant === "AN" ? "blue" : "purple"}>
                    {record.typeProposition}
                </Tag>
            ),
        },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "2" },
        { title: "Organisme", dataIndex: "organisme", key: "3" },
        { title: "Proposé par", dataIndex: "producteur", key: "producteur" },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immat" },
        { title: "Marque", dataIndex: "marque", key: "marque" },
        { title: "Puissance", dataIndex: "puissance", key: "puissance" },
        { title: "Date Effet", dataIndex: "dateEffet", key: "4" },
        { title: "Date Echeance", dataIndex: "dateEcheance", key: "5" },
        {
            title: "Statut",
            dataIndex: "statut",
            key: "statut",
            render: () => <Tag color="default">Corbeille</Tag>,
        },
        { title: "Prime nette", dataIndex: "primeNette", key: "primeNette" },
        { title: "Accessoires", dataIndex: "accessoires", key: "accessoires" },
        { title: "Taxes", dataIndex: "taxes", key: "taxes" },
        { title: "Prime TTC", dataIndex: "primeTotale", key: "primeTotale" },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "7",
            render: (_, record) => (
                <Space>
                    <Tooltip title="Détails">
                        <button
                            type="button"
                            className="btn btn-primary btn-icon rounded-pill"
                            onClick={() => navigate(`/details-contrat/${record.codeId}`)}
                        >
                            <i className="bx bx-dots-vertical-rounded"></i>
                        </button>
                    </Tooltip>
                    <Popconfirm
                        title="Restaurer cette proposition ?"
                        description="Elle reviendra dans la liste des propositions en cours."
                        okText="Oui, restaurer"
                        cancelText="Annuler"
                        okButtonProps={{ loading: restoreLoadingId === record.codeId }}
                        onConfirm={() => handleRestore(record.codeId)}
                    >
                        <Tooltip title="Restaurer">
                            <Button
                                shape="circle"
                                icon={<RollbackOutlined />}
                                loading={restoreLoadingId === record.codeId}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const fetchData = async (params = {}) => {
        setLoadingContrats(true);
        try {
            const response = await axiosInstance.get("/api/contrat/corbeille", {
                params: {
                    page: params.pagination.current - 1,
                    size: params.pagination.pageSize,
                },
            });
            const { content, totalElements } = response.data;
            const uniqueContrats = Array.from(
                new Map((content || []).map((contrat) => [contrat.codeId, contrat])).values()
            );
            const ctrs = uniqueContrats.map((contrat, key) => ({
                key: contrat.codeId,
                codeId: contrat.codeId,
                id: key,
                numeroPolice: contrat.numeroPoliceM ? contrat.numeroPoliceM : contrat.numeroPoliceP,
                souscripteur: contrat?.prenom
                    ? `${contrat?.prenom} ${contrat?.nom}`
                    : `${contrat?.nom || ""}`,
                organisme: contrat?.raisonSociale,
                producteur: contrat?.producteur || "",
                codeAvenant: contrat?.codeAvenant || "",
                typeProposition:
                    TYPE_AVENANT_LABELS[contrat?.codeAvenant] ||
                    contrat?.avenant ||
                    contrat?.codeAvenant ||
                    "—",
                immatriculation: contrat?.immatriculation || "",
                marque: contrat?.marque || "",
                puissance: contrat?.puissance || "",
                dateEffet: contrat?.dateEffet ? `${frenchDate(contrat?.dateEffet)}` : "",
                dateEcheance: contrat?.dateEcheance ? `${frenchDateSimple(contrat?.dateEcheance)} ` : "",
                primeTotale: new Intl.NumberFormat("en-DE", { maximumFractionDigits: 0 }).format(contrat?.primeTotale),
                taxes: new Intl.NumberFormat("en-DE", { maximumFractionDigits: 0 }).format(contrat?.primeTotale * 0.06 / 1.06),
                primeNette: new Intl.NumberFormat("en-DE", { maximumFractionDigits: 0 }).format(contrat?.primeNette),
                accessoires: new Intl.NumberFormat("en-DE", { maximumFractionDigits: 0 }).format(contrat?.accessoires),
                data: contrat,
            }));
            setData(ctrs);
            setPagination({ ...params.pagination, total: totalElements });
        } catch (error) {
            console.error("Failed to fetch corbeille data:", error);
        } finally {
            setLoadingContrats(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (pag) => {
        fetchData({ pagination: pag });
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="mb-0">Corbeille des propositions</h5>
                                    <small className="text-muted">
                                        Propositions annulées manuellement ou déplacées automatiquement après expiration du délai.
                                    </small>
                                </div>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingContrats}>
                                        <Table
                                            columns={columns}
                                            rowKey="codeId"
                                            dataSource={data}
                                            onChange={handleChange}
                                            pagination={pagination}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default CorbeillePropositions;
